/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as TextCopyActionCreators from '@app/actions/TextCopyActionCreators';
import styles from '@app/components/auth/HandoffCodeDisplay.module.css';
import {Button} from '@app/components/uikit/button/Button';
import i18n from '@app/I18n';
import {Trans} from '@lingui/react/macro';
import {CheckCircleIcon, ClipboardIcon, WarningIcon} from '@phosphor-icons/react';
import {useCallback, useEffect, useRef, useState} from 'react';

interface HandoffCodeDisplayProps {
	code: string | null;
	expiresAt: string | null;
	isGenerating: boolean;
	error: string | null;
	onRetry?: () => void;
}

function useCountdown(expiresAt: string | null): number | null {
	const [remaining, setRemaining] = useState<number | null>(() => {
		if (!expiresAt) return null;
		return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
	});

	const expiresAtRef = useRef(expiresAt);

	useEffect(() => {
		if (expiresAt !== expiresAtRef.current) {
			expiresAtRef.current = expiresAt;
			if (expiresAt) {
				setRemaining(Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000)));
			} else {
				setRemaining(null);
			}
		}
	}, [expiresAt]);

	useEffect(() => {
		if (remaining == null || remaining <= 0) return;

		const timer = setInterval(() => {
			const now = Date.now();
			const target = new Date(expiresAtRef.current!).getTime();
			const next = Math.max(0, Math.ceil((target - now) / 1000));
			setRemaining(next);
			if (next <= 0) {
				clearInterval(timer);
			}
		}, 500);

		return () => clearInterval(timer);
	}, [remaining]);

	return remaining;
}

export function HandoffCodeDisplay({code, expiresAt, isGenerating, error, onRetry}: HandoffCodeDisplayProps) {
	const [copied, setCopied] = useState(false);
	const [warningAcknowledged, setWarningAcknowledged] = useState(false);
	const remaining = useCountdown(warningAcknowledged ? expiresAt : null);
	const isExpired = remaining != null && remaining <= 0;

	const handleCopyCode = useCallback(async () => {
		if (!code) return;
		await TextCopyActionCreators.copy(i18n, code);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [code]);

	const handleAcknowledge = useCallback(() => {
		setWarningAcknowledged(true);
	}, []);

	const handleRetry = useCallback(() => {
		setWarningAcknowledged(false);
		setCopied(false);
		onRetry?.();
	}, [onRetry]);

	if (isGenerating) {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Generating code...</Trans>
				</h1>
				<div className={styles.spinner}>
					<span className={styles.spinnerIcon} />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Something went wrong</Trans>
				</h1>
				<p className={styles.error}>{error}</p>
				{onRetry && (
					<Button onClick={handleRetry} fitContainer>
						<Trans>Try again</Trans>
					</Button>
				)}
			</div>
		);
	}

	if (!code) {
		return null;
	}

	if (isExpired) {
		return (
			<div className={styles.container}>
				<h1 className={styles.title}>
					<Trans>Code expired</Trans>
				</h1>
				<p className={styles.expiredDescription}>
					<Trans>This handoff code has expired. Generate a new one to continue.</Trans>
				</p>
				{onRetry && (
					<Button onClick={handleRetry} fitContainer>
						<Trans>Generate new code</Trans>
					</Button>
				)}
			</div>
		);
	}

	if (!warningAcknowledged) {
		return (
			<div className={styles.container}>
				<div className={styles.warningBox}>
					<WarningIcon size={32} weight="fill" className={styles.warningIcon} />
					<h1 className={styles.warningTitle}>
						<Trans>Hold on – keep this code secret</Trans>
					</h1>
					<p className={styles.warningDescription}>
						<Trans>
							Attackers may try to trick you into sharing this code. Only ever enter it into a Fluxer login form when
							signing in to another Fluxer client you control. Never share it with anyone or paste it anywhere else.
						</Trans>
					</p>
					<Button onClick={handleAcknowledge} variant="danger-primary" fitContainer>
						<Trans>I understand, show my code</Trans>
					</Button>
				</div>
			</div>
		);
	}

	const codeWithoutHyphen = code.replace(/-/g, '');
	const codePart1 = codeWithoutHyphen.slice(0, 5);
	const codePart2 = codeWithoutHyphen.slice(5, 10);

	return (
		<div className={styles.container}>
			<h1 className={styles.title}>
				<Trans>Your code is ready!</Trans>
			</h1>
			<p className={styles.description}>
				<Trans>Paste it where you came from to complete sign-in.</Trans>
			</p>

			<div className={styles.codeSection}>
				<p className={styles.codeLabel}>
					<Trans>Your code</Trans>
				</p>
				<div className={styles.codeDisplay}>
					<span className={styles.codeChar}>{codePart1}</span>
					<span className={styles.codeSeparator}>-</span>
					<span className={styles.codeChar}>{codePart2}</span>
				</div>
				<div className={styles.timer}>
					<Trans>Expires in {remaining}s</Trans>
				</div>
				<Button
					type="button"
					onClick={handleCopyCode}
					leftIcon={copied ? <CheckCircleIcon size={16} weight="bold" /> : <ClipboardIcon size={16} />}
					variant="secondary"
				>
					{copied ? <Trans>Copied!</Trans> : <Trans>Copy code</Trans>}
				</Button>
			</div>
		</div>
	);
}
